import React, { useEffect, useMemo, useState } from "react";

import {
  ArrayUtils,
  createFootprintCalculationSummary,
  getYearArray,
  isPercentageComplete,
  queryIsLoading,
  sumFootprintCalculationSummaries,
  useGetBasicDetailsForManyFields,
  useGetCarbonProgramByFarmId,
  useGetFarmDetails,
  useGetFootprintingCalculatedCropFootprints,
  useGetFootprintingCalculatedFarmFootprints,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprints,
  useGetPracticeCrops,
  useLocation,
  useTranslation,
  useUserContext,
} from "utils";

import { REGIONAL_COMPARISON_CROP_MAPPINGS } from "consts";

import { BasicFieldDetails, FootprintCalculationSummary } from "models";

import { BarChart, Card, CheckboxSegmented, Flex, Icon, LineChart, MainPage, OutlinedCard, PageSpin, SelectSegmented } from "components";

import { FootprintingPieChart } from "pages/app/footprinting/components/resultsComponents/FootprintingPieChart";
import { FootprintingResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingResultTable";
import { SummaryTiles } from "pages/app/footprinting/components/resultsComponents/SummaryTiles";

const FootprintingResultsPage: React.FC = () => {
  const { t } = useTranslation([]);
  const { user } = useUserContext();
  const location = useLocation();
  const { cropId: stateCropId, year: stateYear } = location.state || {};

  const farmId = user?.farm.id ?? undefined;
  const farmDetailsQuery = useGetFarmDetails({ farmId });
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const cropDetailsQuery = useGetPracticeCrops({ enabled: true });

  const currentYear = new Date().getFullYear();
  // const availableYears = getYearArray(carbonProgramQuery.data?.farmLevelFootprinting?.startYear ?? currentYear, currentYear);
  const availableYears = getYearArray(carbonProgramQuery.data?.farmLevelFootprinting?.startYear ?? currentYear, currentYear);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(stateCropId || null);
  const [selectedYear, setSelectedYear] = useState(
    stateYear && availableYears.includes(parseInt(stateYear)) ? parseInt(stateYear) : carbonProgramQuery.data?.farmLevelFootprinting?.startYear ?? currentYear
  );

  const carbonProgramHasResultsEnabled =
    carbonProgramQuery.data?.platformFeatures.footprinting?.enabled && (carbonProgramQuery.data.platformFeatures.footprinting.results ?? true);

  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, availableYears, carbonProgramQuery.data?.farmLevelFootprinting?.programCrops, {
    enabled: carbonProgramHasResultsEnabled,
  });
  const farmCalculationQueries = useGetFootprintingCalculatedFarmFootprints(farmId, availableYears, { enabled: carbonProgramHasResultsEnabled });
  const farmProgressQueries = useGetFootprintingFarmFootprints(farmId, availableYears, { enabled: carbonProgramHasResultsEnabled });

  const completedCropProgressQueries = cropProgressQueries.filter((cpq) => cpq.data && isPercentageComplete(cpq.data.completionPct));
  const completedCropCalculationQueries = useGetFootprintingCalculatedCropFootprints(
    farmId,
    availableYears,
    completedCropProgressQueries.map((cpq) => cpq.data?.cropId).filter((id) => id !== undefined),
    { enabled: carbonProgramHasResultsEnabled }
  );

  const fieldBasicDetailsQueries = useGetBasicDetailsForManyFields(
    completedCropProgressQueries.flatMap((cpq) => cpq.data?.footprint?.fieldIds ?? []),
    {
      enabled: !completedCropProgressQueries.some(queryIsLoading),
      includeDisabledFields: true,
    }
  );

  const fieldBasicDetailsByFieldId = useMemo(
    () =>
      fieldBasicDetailsQueries.some(queryIsLoading)
        ? {}
        : ArrayUtils.indexBy(fieldBasicDetailsQueries.filter((fbdq) => fbdq.data).map((fbdq) => fbdq.data) as BasicFieldDetails[], (fbd) => fbd?.id as string),
    [fieldBasicDetailsQueries]
  );

  const isLoading =
    !user?.farm ||
    !user?.farm?.id ||
    [
      carbonProgramQuery,
      cropDetailsQuery,
      ...completedCropProgressQueries,
      ...completedCropCalculationQueries,
      ...farmCalculationQueries,
      ...fieldBasicDetailsQueries,
    ].some(queryIsLoading);

  useEffect(() => {
    if (completedCropProgressQueries.length === 1 && selectedCropId !== completedCropProgressQueries[0].data?.cropId) {
      setSelectedCropId(completedCropProgressQueries[0].data?.cropId ?? null);
    }
  }, [completedCropProgressQueries, selectedCropId]);

  const footprintCalculationSummary = useMemo(() => {
    const selectedFarmCalculation = farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data;
    if (isLoading || !farmDetailsQuery.data || !selectedFarmCalculation) {
      return null;
    }

    if (selectedCropId !== null) {
      const selectedCropCalculation = completedCropCalculationQueries.find((ccq) => ccq.data?.year === selectedYear && ccq.data.cropId === selectedCropId)
        ?.data;
      const selectedCropProgress = completedCropProgressQueries.find((cpq) => cpq.data?.year === selectedYear && cpq.data?.cropId === selectedCropId)?.data;

      return createFootprintCalculationSummary(
        farmDetailsQuery.data,
        selectedCropProgress?.footprint?.fieldIds.map((fid) => fieldBasicDetailsByFieldId[fid]) ?? [],
        selectedFarmCalculation,
        selectedCropCalculation,
        selectedCropProgress?.footprint
      );
    }

    const [totalYieldTonnes, totalFootprintedHectares] = completedCropProgressQueries
      .filter((ccq) => ccq.data !== undefined)
      .map((cpq) => ({
        cropYieldPerHa: cpq.data?.footprint?.totalWeightTonsPerHa ?? 1,
        cropHa: cpq.data?.footprint?.fieldIds.reduce((total, fid) => total + fieldBasicDetailsByFieldId[fid].size, 0) ?? 1,
      }))
      .reduce(
        ([totalYieldTonnes, totalHectares], { cropYieldPerHa: yieldPerHa, cropHa: totalCropHa }) => [
          totalYieldTonnes + yieldPerHa * totalCropHa,
          totalHectares + totalCropHa,
        ],
        [0, 0]
      );

    if (totalYieldTonnes < 1) {
      return null;
    }

    return sumFootprintCalculationSummaries(
      completedCropCalculationQueries
        .filter((ccq) => ccq.data !== undefined)
        .map((ccq) => {
          const progressQuery = completedCropProgressQueries.find((cpq) => cpq.data?.year === selectedYear && cpq.data.cropId === ccq.data.cropId);
          return createFootprintCalculationSummary(
            farmDetailsQuery.data,
            progressQuery?.data?.footprint?.fieldIds.map((fieldId) => fieldBasicDetailsByFieldId[fieldId]),
            selectedFarmCalculation,
            ccq.data,
            progressQuery?.data?.footprint
          );
        })
        .filter((calcSummary) => calcSummary !== null),
      1000 * totalYieldTonnes,
      totalFootprintedHectares
    );
  }, [
    completedCropCalculationQueries,
    completedCropProgressQueries,
    farmCalculationQueries,
    farmDetailsQuery.data,
    fieldBasicDetailsByFieldId,
    isLoading,
    selectedCropId,
    selectedYear,
  ]);

  // TODO: Refactor footprintCalculationSummary to select one here rather than
  // recomputing above
  const selectedCropCalculationSummaries = useMemo(
    () =>
      completedCropProgressQueries
        .filter((cpq) => cpq.data !== undefined && cpq.data.cropId === selectedCropId && isPercentageComplete(cpq.data?.completionPct ?? 0))
        .map((cpq) => {
          const ccq = completedCropCalculationQueries.find((ccq) => ccq.data?.cropId === cpq.data?.cropId && cpq.data?.year === ccq.data?.year);
          const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === cpq.data?.year);
          if (!farmDetailsQuery.data || !cpq.data || !ccq || !fcq) {
            return { year: cpq.data!.year, footprint: null };
          }

          return {
            year: cpq.data.year,
            cropName: cropDetailsQuery.data?.find((cd) => cd.id === cpq.data.cropId)?.name,
            footprint: createFootprintCalculationSummary(
              farmDetailsQuery.data,
              cpq?.data?.footprint?.fieldIds.map((fieldId) => fieldBasicDetailsByFieldId[fieldId]),
              fcq.data,
              ccq.data,
              cpq?.data?.footprint
            ),
          };
        })
        .filter((r) => r.footprint !== null)
        .sort((a, b) => a.year - b.year) as Array<{ year: number; cropName: string; footprint: FootprintCalculationSummary }>,
    [
      completedCropCalculationQueries,
      completedCropProgressQueries,
      cropDetailsQuery.data,
      farmCalculationQueries,
      farmDetailsQuery.data,
      fieldBasicDetailsByFieldId,
      selectedCropId,
    ]
  );

  const cropMapping = REGIONAL_COMPARISON_CROP_MAPPINGS.find((c) => c.id === selectedCropId);
  const regionalValue = cropMapping?.regionalCropComparisonValueCO2ekg;
  const regionalValueComparisonChange =
    regionalValue != null && footprintCalculationSummary?.overall.perKgYieldCO2e != null
      ? ((footprintCalculationSummary.overall.perKgYieldCO2e - regionalValue) / regionalValue) * 100
      : undefined;

  if (isLoading) {
    return (
      <Flex justify="center">
        <PageSpin />
      </Flex>
    );
  }

  if (!carbonProgramQuery.data?.farmLevelFootprinting) {
    return (
      <Flex justify="center">
        <h1>{t("footprinting:footprintingNotEnabled")}</h1>
      </Flex>
    );
  }

  const navigationCrumbs = {
    crumbs: [
      { name: t("global:header.home"), link: "/home" },
      { name: user.farm.name || t("modules:navigation.overview.farm") },
      { name: t("footprinting:footprintingLandingPage.breadcrumb"), link: "footprinting" },
      { name: t("footprinting:resultsPage.breadcrumb") },
    ],
  };

  return (
    <MainPage pageName="Footprinting" navigationCrumbs={navigationCrumbs} showMobileAlert fullWidth className="footprint-detailed-breakdown-wrapper">
      <h1 className="margin-bottom-100">{t("footprinting:resultsPage.title")}</h1>
      <Flex className="margin-bottom-100" justify="space-between">
        <Flex gap={8}>
          {completedCropProgressQueries.length > 1 ? (
            <CheckboxSegmented key={0} checked={selectedCropId === null} label="All" onClick={() => setSelectedCropId(null)} />
          ) : null}
          {completedCropProgressQueries
            .filter((cpq) => cpq.data?.year === selectedYear)
            .map((cqp) => {
              const cropInfo = cropDetailsQuery.data?.find((cd) => cd.id === cqp.data?.cropId);
              return cropInfo ? (
                <CheckboxSegmented
                  checked={cropInfo.id === selectedCropId}
                  key={`${cqp.data?.year}-${cropInfo?.name}`}
                  label={cropInfo?.name}
                  onClick={() => setSelectedCropId(cropInfo?.id)}
                  disabled={completedCropProgressQueries.length === 1}
                />
              ) : null;
            })}
        </Flex>
        <SelectSegmented
          value={selectedYear}
          onChange={setSelectedYear}
          options={availableYears
            .filter((year) => {
              if (carbonProgramQuery.data.farmLevelFootprinting?.disabledYears?.includes(year)) {
                return false;
              }

              // Only show year options if that year has a valid footprint to show
              const farmCompleteForYear = farmProgressQueries.some((fpq) => fpq.data?.year === year && isPercentageComplete(fpq.data?.completionPct ?? 0));
              const someCropCompleteForYear = cropProgressQueries.some((cpq) => cpq.data?.year === year && isPercentageComplete(cpq.data?.completionPct ?? 0));
              return farmCompleteForYear && someCropCompleteForYear;
            })
            .map((y) => ({ label: y.toString(), value: y }))}
        />
      </Flex>

      <Flex vertical className="margin-bottom-100">
        <SummaryTiles footprintCalculationSummary={footprintCalculationSummary ?? undefined} hidePerYield={selectedCropId === null} farmId={farmId} />
      </Flex>

      <Flex gap={16}>
        <Card className="container large">
          <OutlinedCard className="footprint-detailed-breakdown-card" dataTestId="footprint-detailed-breakdown-card">
            <div>
              <h3>{t("footprinting:resultsPage.detailedBreakdownTitle")}</h3>
              <p className="paragraph-s grey-text">{t("footprinting:resultsPage.detailedBreakdownSubtitle")}</p>
            </div>

            <div className="margin-top-200 footprint-detailed-breakdown-grid">
              <Flex justify="center" className="padding-top-100">
                <FootprintingPieChart footprintCalculationSummary={footprintCalculationSummary ?? undefined} />
              </Flex>

              <FootprintingResultTable hidePerYield={selectedCropId === null} footprintCalculationSummary={footprintCalculationSummary ?? undefined} />
            </div>
          </OutlinedCard>
        </Card>
        <Flex vertical gap={16}>
          <div className="padding-125 footprinting-results-graph">
            <BarChart
              width={320}
              height={340}
              title={t("footprinting:resultsPage.barChart.title")}
              subtitle={t("footprinting:resultsPage.barChart.subtitle")}
              tag={
                <Flex align="center" gap={4} className="pct-bubble">
                  <Icon iconName={regionalValueComparisonChange! >= 0 ? "tabler/trending-up" : "tabler/trending-down"} color="white" className="icon inline" />
                  {Math.abs(regionalValueComparisonChange!).toFixed(1)}%
                </Flex>
              }
              bars={[
                {
                  label: `${t("footprinting:resultsPage.barChart.yourResultLabel")}`,
                  value: footprintCalculationSummary?.overall.perKgYieldCO2e || 0,
                  color: "#065C45", // green
                },
                {
                  label: `${t("footprinting:resultsPage.barChart.regionalLabel")}`,
                  value: regionalValue || 0,
                  color: "#B4A8C8", // purple
                },
              ]}
              formatFunction={(value: number) => `${value.toFixed(2)}`}
            />
          </div>
          <div className="padding-125 footprinting-results-graph">
            <LineChart
              labels={selectedCropCalculationSummaries.map((summary) => summary.year.toString())}
              width={320}
              height={340}
              title={t("footprinting:resultsPage.yearOnYearComparison.title")}
              subtitle={t("footprinting:resultsPage.yearOnYearComparison.subtitle")}
              lines={
                selectedCropCalculationSummaries.length > 0
                  ? [
                      {
                        label: selectedCropCalculationSummaries[0].cropName,
                        points: selectedCropCalculationSummaries.map((summary) => ({
                          value: summary.footprint.overall.perKgYieldCO2e,
                        })),
                      },
                    ]
                  : []
              }
            />
          </div>
        </Flex>
      </Flex>
    </MainPage>
  );
};

export default FootprintingResultsPage;
